import { forwardRef, useId } from 'react'

import { Switch as SwitchPrimitive } from '@base-ui/react/switch'
import { tv, type VariantProps } from 'tailwind-variants'

import { cn } from '../../../utils/cn'
import { labelSizeToVariant } from '../../atoms/label/label'
import { Text } from '../../atoms/text/text'

// The button is the comp's full hit box (the Ømni glyph's bounding box,
// larger than the visible track).
const boxVariants = tv({
  base: [
    'group inline-flex shrink-0 cursor-pointer items-center justify-center rounded-full',
    'focus-visible:ring-primary-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
    'data-[disabled]:cursor-not-allowed'
  ],
  variants: {
    size: {
      sm: 'h-[18px] w-[29px]',
      md: 'h-[21px] w-[34px]',
      lg: 'h-[24px] w-[42px]'
    }
  },
  defaultVariants: { size: 'md' }
})

/* The comp's toggle is the Material Symbols glyph, where the thumb is a HOLE
 * punched through the track, showing whatever is behind it. Reproduced with a
 * two-layer mask — a thumb-sized circle excluded from a full layer (the
 * accordion's mask-position technique) — slid between the edges on toggle.
 * Track sizes are measured from the glyph paths; the glyph's own relation
 * (thumb Ø = track height / 2, inset = Ø / 2) is derived, not restated. */
const trackVariants = tv({
  base: [
    'pointer-events-none block rounded-full',
    'h-(--track-h) w-(--track-w)',
    '[--inset:calc(var(--thumb)/2)] [--thumb:calc(var(--track-h)/2)]',
    'bg-surface-body group-data-[checked]:bg-primary group-data-[disabled]:bg-surface-disabled!',
    '[mask-image:radial-gradient(circle_closest-side,#000_calc(100%-0.5px),transparent_100%),linear-gradient(#000_0_0)]',
    '[mask-size:var(--thumb)_var(--thumb),100%_100%]',
    '[mask-composite:exclude] [mask-repeat:no-repeat]',
    /* 2-value positions only: Chrome's mask-position parser rejects the
     * 3-value edge-offset syntax (`left 3.5px center`), dropping the whole
     * declaration. The 100% here resolves against (track − hole), so the
     * calc lands the hole exactly --inset from the right edge. */
    '[mask-position:var(--inset)_50%,0_0]',
    'group-data-[checked]:[mask-position:calc(100%_-_var(--inset))_50%,0_0]',
    'transition-[mask-position,background-color] duration-200 ease-out motion-reduce:transition-none'
  ],
  variants: {
    size: {
      sm: '[--track-h:14.1px] [--track-w:26.9px]',
      md: '[--track-h:15.5px] [--track-w:29.7px]',
      lg: '[--track-h:19.2px] [--track-w:36.6px]'
    }
  },
  defaultVariants: { size: 'md' }
})

export type SwitchSize = NonNullable<VariantProps<typeof boxVariants>['size']>

export type SwitchProps = React.ComponentProps<typeof SwitchPrimitive.Root> & {
  size?: SwitchSize
  label?: string
  /** Secondary line under the label. Rendered only when `label` is set. */
  hint?: string
  /** Which side of the text the toggle sits on. Defaults to `left`. */
  togglePosition?: 'left' | 'right'
}

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(
  (
    { size = 'md', className, label, hint, togglePosition = 'left', ...props },
    ref
  ) => {
    const hintId = useId()
    const hasHint = Boolean(label && hint)
    const describedBy =
      [hasHint ? hintId : null, props['aria-describedby']]
        .filter(Boolean)
        .join(' ') || undefined

    const switchEl = (
      <SwitchPrimitive.Root
        ref={ref}
        data-slot='switch'
        aria-label={label}
        className={cn(boxVariants({ size }), className)}
        {...props}
        aria-describedby={describedBy}
      >
        <span data-slot='switch-track' className={trackVariants({ size })} />
      </SwitchPrimitive.Root>
    )

    if (!label) return switchEl

    return (
      /* The state lives on the Root button, and the Texts are its
       * grandchildren (nested in the flex-col span) — out of reach of peer-*
       * sibling selectors — so the wrapper reads it through group-has. */
      <label
        className={cn(
          'group/switch inline-flex cursor-pointer select-none items-start gap-2 has-[[data-disabled]]:cursor-not-allowed',
          togglePosition === 'right' && 'flex-row-reverse'
        )}
      >
        {switchEl}
        <span className='flex flex-col gap-1'>
          <Text
            as='span'
            variant={labelSizeToVariant[size]}
            data-text={label}
            className={cn(
              'text-surface-title-50',
              'group-has-[[data-checked]]/switch:font-semibold',
              'group-has-[[data-disabled]]/switch:text-content-disabled',
              /* Ghost duplicate of the label reserves the semibold width, so
               * toggling doesn't reflow the row (checkbox's pattern). */
              'after:invisible after:block after:h-0 after:overflow-hidden after:font-semibold after:content-[attr(data-text)]'
            )}
          >
            {label}
          </Text>
          {hint && (
            <Text
              as='span'
              id={hintId}
              variant='caption-md'
              weight='semibold'
              className='text-surface-body group-has-[[data-disabled]]/switch:text-content-disabled-muted'
            >
              {hint}
            </Text>
          )}
        </span>
      </label>
    )
  }
)
Switch.displayName = 'Switch'
